/**
 * Pratica submit schemas — F-003.4 Phase 2 (IMPL-P2-001).
 *
 * Zod schemi per le 3 nuove route della pratica:
 *  - `pdfUrlRequestSchema`     → POST /:id/pdf/url     (body vuoto, praticaId in path)
 *  - `pdfConfirmRequestSchema` → POST /:id/pdf/confirm (etag opzionale + sizeBytes)
 *  - `submitRequestSchema`     → POST /:id/submit      (consenso esplicito + hash testo)
 *
 * Tutti gli schemi sono `.strict()` per bloccare chiavi extra (anti-tampering)
 * e per costringere il frontend a inviare solo i campi documentati.
 *
 * Pattern di riferimento: `packages/shared/src/schemas/contract.ts`
 *  - export named per ogni schema
 *  - `z.infer<typeof schema>` esportato come tipo TS
 *  - JSDoc per ogni costante esportata
 */
import { z } from 'zod';
/**
 * POST /api/v1/pratiche/:id/pdf/url — body vuoto.
 *
 * Il `praticaId` arriva dal path; non serve alcun payload. Lo schema è
 * comunque definito (e `.strict()`) per:
 *  - bloccare chiavi extra che il client potrebbe inviare per errore
 *  - mantenere parità API con gli altri 2 schemi (validate uniforme)
 */
export const pdfUrlRequestSchema = z.object({}).strict();
/**
 * POST /api/v1/pratiche/:id/pdf/confirm — conferma upload S3.
 *
 * Campi:
 *  - `etag`      → opzionale, ETag restituito da S3 dopo il PUT (alcuni
 *                  client lo persistono come `pdfSha256` se l'header MD5 è
 *                  configurato). Se assente, il server usa l'ETag dal HEAD.
 *  - `sizeBytes` → dimensione percepita lato client (info, non autoritativa:
 *                  la verifica autoritativa è fatta server-side via HEAD).
 *                  Limite max 10 MB (10 * 1024 * 1024) come da specifica.
 */
export const pdfConfirmRequestSchema = z
    .object({
    etag: z.string().optional(),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .max(10 * 1024 * 1024, { message: 'PDF size exceeds 10MB limit' }),
})
    .strict();
/**
 * POST /api/v1/pratiche/:id/submit — submit pratica con consenso atomico.
 *
 * Campi:
 *  - `consentGiven`     → DEVE essere `true` letterale (z.literal(true)).
 *                         Niente checkbox accidentale: il frontend invia
 *                         `true` SOLO dopo che l'utente ha attivamente
 *                         flaggato la `ResponsibilityCheckbox`.
 *  - `consentTextHash`  → SHA-256 hex del testo del consenso mostrato.
 *                         Il frontend calcola il hash con `SubtleCrypto`
 *                         e lo invia per immutabilità del consenso (audit).
 *                         Regex `/^[a-f0-9]{64}$/` enforce 64 hex chars.
 */
export const submitRequestSchema = z
    .object({
    consentGiven: z.literal(true),
    consentTextHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/, { message: 'Must be a SHA-256 hex string' }),
})
    .strict();
//# sourceMappingURL=pratica-submit.js.map